<template>
  <div id="call-records-view" class="page-table scrollable">
    <el-dialog
      width="440px"
      :visible="modalVisible"
      :before-close="handleCloseModal"
      :close-on-click-modal="false"
    >
      <h2 class="form-section-header">Filter Settings</h2>

      <h3 class="black-text mb-5">Select Date Range</h3>
      <el-date-picker
        class="mb-10"
        style="margin-left: auto"
        v-model="historyDateRange"
        type="datetimerange"
        align="right"
        unlink-panels
        format="yyyy-MM-dd HH:mm"
        :clearable="false"
        range-separator="To"
        start-placeholder="Start date"
        end-placeholder="End date"
        :picker-options="datePickerOptions"
      >
      </el-date-picker>

      <el-form id="stats-advanced-modal" class="form-modal">
        <h3 class="black-text mb-10">Select Distribution</h3>
        <el-radio-group v-model="distribution">
          <el-radio-button label="hourly">Hourly</el-radio-button>
          <el-radio-button type="default" label="daily">Daily</el-radio-button>
          <el-radio-button label="monthly">Monthly</el-radio-button>
          <el-radio-button label="none">None</el-radio-button>
        </el-radio-group>

        <h3 class="black-text mb-0">Filter By</h3>
        <el-select
          v-model="primaryFilter"
          placeholder="Select Filter"
          clearable
          style="margin-top: 0"
        >
          <template slot="prefix"> Filter By </template>
          <el-option value="ivrs" label="Virtual Receptionists" />
        </el-select>

        <el-select
          v-model="filterIvrs"
          v-if="primaryFilter === 'ivrs'"
          value-key="id"
          placeholder="Select ivrs"
          clearable
          multiple
          filterable
          style="margin-top: 0"
        >
          <el-option
            v-for="item in ivrOptions"
            :key="item.id"
            :value="item.id"
            :label="item.name"
          />
        </el-select>

        <el-button class="mt-20" @click="doAdvanced"> Update </el-button>
      </el-form>
    </el-dialog>

    <div id="users-stats-view" class="no-table-box page-header">
      <div class="flex justify-space-between mb-20">
        <h2 class="mb-0 mt-0 text-left">
          <v-svg sprite="receptionist" class="voxo-purple"></v-svg>
          IVR Summary
        </h2>
        <el-tag>
          <i class="el-icon-info" style="margin-right: 4px"></i>
          Total IVRs: {{ total }}
        </el-tag>
      </div>

      <div class="flex align-center mb-20">
        <el-radio-group
          v-model="presetSelection"
          @change="doPreset"
          class="hidden-xs-only"
        >
          <el-radio-button type="default" label="yesterday"
            >Yesterday</el-radio-button
          >
          <el-radio-button type="default" label="last10"
            >Last 10 Days</el-radio-button
          >
          <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
        </el-radio-group>

        <el-tooltip placement="top">
          <template slot="content">
            {{ selectedDateRange }}
            <br />
            Dist: {{ distribution[0].toUpperCase() + distribution.slice(1) }}
          </template>
          <el-button @click="modalVisible = true" class="ml-10">
            Filter
          </el-button>
        </el-tooltip>
        <el-button
          @click="resetTable"
          v-if="advancedActive"
          style="background-color: #f25a6b; color: #fff"
        >
          Reset
        </el-button>
        <el-button class="ml-10" @click="doExport"> Export </el-button>
      </div>

      <div class="vxe-table-height-tabs card-base card-shadow--medium">
        <vxe-grid
          round
          class="user-stats-table"
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="loadingData"
          :columns="
            this.distribute
              ? [this.expandColumn, ...this.tableColumns]
              : this.tableColumns
          "
        >
        </vxe-grid>
      </div>
    </div>
  </div>
</template>

<script>
import feathersClient from "@/feathers/feathers-client";
import moment from "moment-timezone";
import { ExportToCsv } from "export-to-csv";

export default {
  name: "IVRSummary",
  props: {
    ivrOptions: { required: true },
  },
  data() {
    return {
      exportCSV: false,
      distribute: false,
      presetSelection: "last10",
      advancedActive: false,
      loadingData: false,
      modalVisible: false,
      ivrIds: [],
      filterIvrs: [],
      noIvrs: true,
      dateStart: null,
      dateEnd: null,
      primaryFilter: null,
      distribution: "none",
      total: 0,
      tableData: [],
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
      expandColumn: {
        type: "expand",
        width: "25px",
        showOverflow: true,
        slots: {
          content: ({ row }) => {
            return [
              <div class="users-stats-distribution-table">
                <vxe-table
                  border="none"
                  show-overflow
                  highlight-hover-row
                  show-header={false}
                  height="300"
                  empty-text="No Data"
                  scroll-y={{ enabled: true, gt: 10, oSize: 0 }}
                  data={row.distributionStats}
                >
                  <vxe-table-column field="name" width="190"></vxe-table-column>
                  <vxe-table-column
                    field="uniqueEntries"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalEntries"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="loopRatio"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="validKeyTotal"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="validKeyRatioTotal"
                    min-width="100"
                    formatter={this.ratioFormatter}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="wrongKeyTotal"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="disaWrongTotal"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="wrongKeyRatioTotal"
                    min-width="100"
                    formatter={this.ratioFormatter}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="disaTotal"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="disaRatioTotal"
                    min-width="100"
                    formatter={this.ratioFormatter}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="hangupTotal"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="hangupRatioTotal"
                    min-width="100"
                    formatter={this.ratioFormatter}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="timeoutTotal"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="timeoutRatioTotal"
                    min-width="100"
                    formatter={this.ratioFormatter}
                  ></vxe-table-column>
                </vxe-table>
              </div>,
            ];
          },
        },
      },
      tableColumns: [
        {
          field: "name",
          title: "Name",
          width: "190",
          showOverflow: true,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.name}</span>];
            },
          },
        },
        {
          field: "uniqueEntries",
          title: "Unique Entries",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.uniqueEntries}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Unique Entries">
                  <v-svg sprite="enter" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalEntries",
          title: "Total Entries",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalEntries}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Total Entries">
                  <v-svg sprite="enter" class="voxo-blue"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "loopRatio",
          title: "Loop Ratio",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.loopRatio}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Loop Ratio">
                  <v-svg sprite="refresh" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "validKeyTotal",
          title: "Valid Key Total",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.validKeyTotal}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Valid Key Total">
                  <v-svg sprite="keypad" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "validKeyRatioTotal",
          title: "Valid Key %",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [
                <span>{(+row.validKeyRatioTotal * 100).toFixed(0)}%</span>,
              ];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Valid Key %">
                  <v-svg sprite="keypad" class="voxo-aqua"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "wrongKeyTotal",
          title: "Wrong Key Total",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.wrongKeyTotal}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Wrong Key Total">
                  <v-svg sprite="keypad" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "disaWrongTotal",
          title: "Wrong Ext Dial",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.disaWrongTotal}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Wrong Ext Dial">
                  <v-svg sprite="phone" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "wrongKeyRatioTotal",
          title: "Wrong Key %",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [
                <span>{(+row.wrongKeyRatioTotal * 100).toFixed(0)}%</span>,
              ];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Wrong Key %">
                  <v-svg sprite="keypad" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "disaTotal",
          title: "Ext Dial Total",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.disaTotal}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Ext Dial Total">
                  <v-svg sprite="phone" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "disaRatioTotal",
          title: "Ext Dial %",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{(+row.disaRatioTotal * 100).toFixed(0)}%</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Ext Dial %">
                  <v-svg sprite="phone" class="voxo-blue"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "hangupTotal",
          title: "Hangups Total",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.hangupTotal}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Hangups Total">
                  <v-svg sprite="call-block" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "hangupRatioTotal",
          title: "Hangup %",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{(+row.hangupRatioTotal * 100).toFixed(0)}%</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Hangup %">
                  <v-svg sprite="call-block" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "timeoutTotal",
          title: "Timeouts Total",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.timeoutTotal}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Timeouts Total">
                  <v-svg sprite="clock" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "timeoutRatioTotal",
          title: "Timeout %",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [
                <span>{(+row.timeoutRatioTotal * 100).toFixed(0)}%</span>,
              ];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Timeout %">
                  <v-svg sprite="clock" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    selectedDateRange() {
      if (this.dateEnd && this.dateStart) {
        const dateStart = moment(this.dateStart).format("M/D/YY HH:mm");
        const dateEnd = moment(this.dateEnd).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    },
  },
  watch: {
    selectedTenant() {
      this.loadingData = true;
    },
  },
  methods: {
    initData() {
      if (this.ivrOptions.length) {
        this.noIvrs = false;
        this.ivrIds = this.ivrOptions.map((item) => item.id);
        this.doPreset("init");
      } else {
        if (this.chart) {
          this.chart.dispose();
        }
        this.noIvrs = true;
      }
    },
    updateDateRangePicker() {
      this.historyDateRange = [moment(this.dateStart), moment(this.dateEnd)];
    },
    doPreset(val) {
      if (val === "init") {
        this.dateEnd = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().startOf("day").format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "last10") {
        this.dateEnd = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().subtract(9, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "lastMonth") {
        this.dateEnd = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "yesterday") {
        this.dateEnd = moment()
          .startOf("day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment()
          .subtract(1, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        this.presetSelection = "yesterday";
        this.updateDateRangePicker();
        this.queryStats();
      }
    },
    handleCloseModal() {
      this.modalVisible = false;
    },
    doAdvanced() {
      this.dateStart = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.dateEnd = moment(this.historyDateRange[1]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      //if hourly distribution make sure it passes 30 day limit
      let allowed = 1;
      if (this.distribution === "hourly") {
        allowed =
          moment(this.dateEnd).diff(
            moment(this.dateStart).add(1, "day"),
            "days"
          ) <= 2;
      }

      if (allowed) {
        this.updateDateRangePicker();
        this.queryStats();
        this.handleCloseModal();
        this.advancedActive = true;
        this.distribute = this.distribution !== "none";
        this.presetSelection = null;
      } else {
        this.$notify({
          type: "error",
          message: "Max of 2 days exceeded for hourly distribution",
          title: "Limit Exceeded",
        });
      }
    },
    resetTable(tenantChanged) {
      this.advancedActive = false;
      this.primaryFilter = "";
      this.filterIvrs = [];
      this.exportCSV = false;
      this.distribution = "none";
      this.distribute = false;
      if (tenantChanged) {
        this.initData();
      } else {
        this.doPreset("init");
      }
    },
    doExport() {
      this.$confirm(`Export data for ${this.total} virtual receptionists?`, {
        type: "warning",
      })
        .then(() => {
          //export from browser
          let csvData = [];
          for (let i = 0; i < this.tableData.length; i++) {
            const csvObject = {};
            for (let key in this.tableData[i]) {
              if (key !== "distributionStats" && key !== "_XID") {
                csvObject[key] = this.tableData[i][key];
              }
            }
            csvData.push(csvObject);

            const distStats = this.tableData[i].distributionStats;
            for (let k = 0; k < distStats.length; k++) {
              const csvObjectDist = {};
              for (let key in distStats[k]) {
                csvObjectDist[key] = distStats[k][key];
              }
              if (Object.keys(csvObjectDist).length)
                csvData.push(csvObjectDist);
            }
          }

          const options = {
            filename: "ivr_summary",
            showLabels: true,
            headers: [
              "Name",
              "Unique Entries",
              "Total Entries",
              "Loop Ratio",
              "Valid Key Press Total",
              "Valid Key Press Ratio",
              "Wrong Key Press Total",
              "Wrong Ext Dials",
              "Wrong Key Press Ratio",
              "Ext Dials",
              "Ext Dial Ratio",
              "Hangups Total",
              "Hangup Ratio",
              "Timeouts Total",
              "Timeout Ratio",
            ],
          };

          const csvExporter = new ExportToCsv(options);
          csvExporter.generateCsv(csvData);
        })
        .catch(() => true);
    },
    queryStats() {
      this.loadingData = true;
      feathersClient
        .service("account-statistics")
        .create({
          type: "ivrSummary",
          tenantId: this.selectedTenant.id,
          ivrIds: this.primaryFilter === "ivrs" ? this.filterIvrs : this.ivrIds,
          dateStart: this.dateStart,
          dateEnd: this.dateEnd,
          distribution: this.distribution,
          primaryFilter: this.primaryFilter,
          timezone: this.user.timezone,
        })
        .then((res) => {
          this.total = res.total;
          this.tableData = res.data;
          this.populateTable();
          this.loadingData = false;
        })
        .catch((res) => {
          this.loadingData = false;
        });
    },
    ratioFormatter(data) {
      return `${(+data.cellValue * 100).toFixed(0)}%`;
    },
    populateTable() {
      if (this.$refs.xGrid) {
        this.$refs.xGrid.loadData(this.tableData);
        const arrowDown = document.querySelectorAll(".vxe-sort--desc-btn");
        const arrowUp = document.querySelectorAll(".vxe-sort--asc-btn");
        const arrows = [...arrowDown, ...arrowUp];
        for (let i = 0; i < arrows.length; i++) {
          arrows[i].removeAttribute("title");
        }
      }
    },
  },
};
</script>
